import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, notification, Space, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';

import { IInvestimentoAtivacao } from '../../../../../../InvestimentosACadastrar/interfaces';
import { stateGlobal } from '../../../../../../../../interfaces/Global';

import { checkHasActiveFilters } from '../../../../../../components/BotaoLimparFiltros';
import FontesTagsCell from '../FontesTagsCell';
import { useFontesColumnSearchProps } from '../../hooks/useFontesColumnSearchProps';
import { useGetColumnSearchProps } from '../../../../../../../../hooks/FilterTable/stringCLearFiltros';
import { useGetColumnSearchPropsObj } from '../../../../../../../../hooks/FilterTable/objString';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../../../../../../services/Conversores';
import SolicitacoesDeAtivacaoService from '../../../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService';
import OrgaoService from '../../../../../../../../services/http/services/SaldoDeContas/OrgaoService';
import { removeAcento } from '../../../../../../../../utils/RemoveAcentos';
import { onlyNumbers } from '../../../../../../utils';

function onlyNumbersAndLetters(value: string) {
    if (!value) {
        return "";
    }
    return value.replace(/[^a-zA-Z0-9]/g, '');
}

export interface IUseInvestimentosTabProps {
    solicitacaoId?: number;
    orgaoId?: number;
    isManutencao?: boolean;
}

export const useInvestimentosTab = ({
    solicitacaoId,
    orgaoId,
    isManutencao,
}: IUseInvestimentosTabProps) => {
    const propsState = useSelector((state: stateGlobal) => state.global);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [investimentos, setInvestimentos] = useState<IInvestimentoAtivacao[]>([]);
    const [loading, setLoading] = useState(false);
    const [investimentoParaAlterar, setInvestimentoParaAlterar] = useState<IInvestimentoAtivacao | null>(null);
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});

    const refContagemDoFiltro = useRef<Array<IInvestimentoAtivacao>>([]);

    const carregarInvestimentos = useCallback(async () => {
        const source = axios.CancelToken.source();
        setLoading(true);

        try {
            let data: IInvestimentoAtivacao[] = [];
            if (isManutencao && orgaoId) {
                data = await OrgaoService.getInvestimentos({ orgaoId, signal: source.token });
            } else if (solicitacaoId) {
                data = await SolicitacoesDeAtivacaoService.getInvestimentos({ solicitacaoId, signal: source.token });
            }
            setInvestimentos(data || []);
        } catch (error) {
            let msgErro: any = error as Error;
            if (msgErro?.message !== 'Requisicao cancelada!') {
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possível carregar os investimentos!',
                });
            }
        } finally {
            setLoading(false);
        }
    }, [isManutencao, orgaoId, solicitacaoId]);

    useEffect(() => {
        carregarInvestimentos();
    }, [carregarInvestimentos]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
    }, []);

    const onclickTable = useCallback((record: IInvestimentoAtivacao): void => {
        if (record.status === 'TOTAL_WITHDRAWAL') {
            return;
        }

        if (selectedRowKeys.includes(record.id)) {
            setSelectedRowKeys([]);
        } else {
            setSelectedRowKeys([record.id]);
        }
    }, [selectedRowKeys]);

    const onSelectChange = useCallback((keys: number[]): void => {
        if (keys.length > 1) {
            setSelectedRowKeys([keys[1]]);
        } else if (keys.length === 0) {
            setSelectedRowKeys([]);
        } else {
            setSelectedRowKeys([keys[0]]);
        }
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: any[]) => onSelectChange(keys as number[]),
        columnWidth: '1%',
        type: 'radio' as const,
        getCheckboxProps: (record: IInvestimentoAtivacao) => ({
            disabled: record.status === 'TOTAL_WITHDRAWAL',
        }),
    };

    const investimentoSelecionado = useMemo(() => {
        if (selectedRowKeys.length === 1) {
            return investimentos.find((e) => e.id === selectedRowKeys[0]) || null;
        }
        return null;
    }, [selectedRowKeys, investimentos]);

    const canAlterarConta = useMemo(() => {
        return !!investimentoSelecionado && investimentoSelecionado.status === 'ACTIVE';
    }, [investimentoSelecionado]);

    const isInvestimentoResgatado = useMemo(() => {
        return !!investimentoSelecionado && investimentoSelecionado.status === 'TOTAL_WITHDRAWAL';
    }, [investimentoSelecionado]);

    const handleClickAlterarConta = useCallback(() => {
        if (investimentoSelecionado && investimentoSelecionado.status === 'ACTIVE') {
            setInvestimentoParaAlterar(investimentoSelecionado);
        }
    }, [investimentoSelecionado]);

    const handleCloseAlterarConta = useCallback(() => {
        setInvestimentoParaAlterar(null);
    }, []);

    const handleSuccessAlterarConta = useCallback(() => {
        carregarInvestimentos();
        handleResetRowSelection();
    }, [carregarInvestimentos, handleResetRowSelection]);

    const refFuncaoLimparFiltrosStringNome = useRef<any>(null);
    const columnSearchPropsNome = useGetColumnSearchProps('nome', refFuncaoLimparFiltrosStringNome);
    const columnSearchPropsFontes = useFontesColumnSearchProps({
        dataIndex: 'fontes',
        getFontes: (record: IInvestimentoAtivacao) => record.conta?.fontes,
        allowSemFontes: false,
    });

    const [searchTextConta, setSearchTextConta] = useState('');
    const [searchedColumnConta, setSearchedColumnConta] = useState('');

    const handleSearchConta = useCallback((selectedKeys: any, confirm: any, dataIndex: any): void => {
        confirm();
        setSearchTextConta(selectedKeys[0]);
        setSearchedColumnConta(dataIndex);
    }, []);

    const handleResetConta = useCallback((clearFilters: any): void => {
        clearFilters();
        setSearchTextConta('');
    }, []);

    const columnSearchPropsContaBancaria = useMemo(() => {
        let searchInput: any = null;
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div
                    style={{ padding: 8 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                            handleSearchConta(selectedKeys, confirm, 'conta');
                        }
                    }}
                >
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder="Pesquisar agência, conta ou banco..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearchConta(selectedKeys, confirm, 'conta')}
                        style={{ width: 230, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearchConta(selectedKeys, confirm, 'conta')}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 95 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleResetConta(clearFilters)} size="small" style={{ width: 95 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: any, record: IInvestimentoAtivacao) => {
                if (!value) return true;
                const rawValue = value.toString();
                const term = removeAcento(rawValue).toLowerCase().trim();
                const words = term.split(/\s+/).filter(Boolean);
                const termDigits = onlyNumbers(rawValue);
                const termAlphanumeric = onlyNumbersAndLetters(rawValue).toLowerCase();

                const bancoNome = removeAcento(record.conta?.banco?.nome || '').toLowerCase();
                const agencia = removeAcento(record.conta?.agencia || '').toLowerCase();
                const agenciaDigits = onlyNumbers(record.conta?.agencia || '');
                const numero = removeAcento(record.conta?.numero || '').toLowerCase();
                const numeroDigits = onlyNumbers(record.conta?.numero || '');
                const numeroAlphanumeric = onlyNumbersAndLetters(record.conta?.numero || '').toLowerCase();
                const agContaText = removeAcento(`ag: ${record.conta?.agencia || ''} conta: ${record.conta?.numero || ''}`).toLowerCase();
                const fullText = `${bancoNome} ${agencia} ${numero} ${agContaText}`;

                const matchBanco =
                    bancoNome.includes(term) ||
                    (words.length > 1 && words.every(w => bancoNome.includes(w)));
                const matchAgencia =
                    agencia.includes(term) ||
                    (termDigits.length > 0 && agenciaDigits.includes(termDigits));
                const matchNumero =
                    numero.includes(term) ||
                    (termDigits.length > 0 && numeroDigits.includes(termDigits)) ||
                    (termAlphanumeric.length > 0 && numeroAlphanumeric.includes(termAlphanumeric));
                const matchAgConta =
                    agContaText.includes(term) ||
                    (words.length > 1 && words.every(w => fullText.includes(w)));

                return matchBanco || matchAgencia || matchNumero || matchAgConta;
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput?.select());
                }
            },
        };
    }, [handleSearchConta, handleResetConta]);

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchTextConta('');
        setSearchedColumnConta('');
        refFuncaoLimparFiltrosStringNome.current?.limparFiltros?.();
        refContagemDoFiltro.current = [];
        handleResetRowSelection();
    }, [handleResetRowSelection]);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchTextConta);
    }, [filteredInfo, searchTextConta]);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'nome',
            key: 'nome',
            filteredValue: filteredInfo.nome || null,
            ...columnSearchPropsNome,
            width: '15%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) => a.nome.localeCompare(b.nome),
            render: (nome: string) => nome,
        },
        {
            title: 'Origem',
            dataIndex: 'origem',
            key: 'origem',
            filteredValue: filteredInfo.origem || null,
            width: '9%',
            filters: [
                { text: 'Open Finance', value: 'OPEN_FINANCE' },
                { text: 'Manual', value: 'MANUAL' },
            ],
            onFilter: (value: any, record: IInvestimentoAtivacao) => record.origem === value,
            render: (origem: string) => (
                <Tag color={origem === 'OPEN_FINANCE' ? 'blue' : 'cyan'}>
                    {origem === 'OPEN_FINANCE' ? 'Open Finance' : 'Manual'}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filteredValue: filteredInfo.status || null,
            width: '11%',
            filters: [
                { text: 'Ativo', value: 'ACTIVE' },
                { text: 'Resgatado', value: 'TOTAL_WITHDRAWAL' },
                { text: 'Conta encerrada', value: 'CONTA_ENCERRADA' },
            ],
            onFilter: (value: any, record: IInvestimentoAtivacao) => {
                if (record.conta.encerradaEm) {
                    return value === 'CONTA_ENCERRADA';
                }

                return record.status === value;
            },
            render: (status: string, record: IInvestimentoAtivacao) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    {!record.conta?.encerradaEm ? (
                        <Tag color={status === 'ACTIVE' ? 'success' : 'default'} style={{ margin: 0 }}>
                            {status === 'ACTIVE' ? 'Ativo' : 'Resgatado'}
                        </Tag>
                    ) : (
                        <Tag color="error" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                            Conta encerrada
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Conta bancária',
            dataIndex: 'conta',
            key: 'conta',
            filteredValue: filteredInfo.conta || null,
            ...columnSearchPropsContaBancaria,
            width: '18%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) =>
                (a.conta?.banco?.nome || '').localeCompare(b.conta?.banco?.nome || ''),
            render: (_: any, record: IInvestimentoAtivacao) => {
                if (!record.conta) {
                    return '-';
                }

                const agContaText = `Ag: ${record.conta.agencia || '-'} | Conta: ${record.conta.numero || '-'}`;
                const bancoNome = record.conta.banco?.nome || '-';
                const descricao = record.conta.descricao;

                const isSearched = searchedColumnConta === 'conta' && !!searchTextConta;
                const searchWords = isSearched
                    ? [searchTextConta, ...searchTextConta.split(/\s+/).filter(Boolean), onlyNumbers(searchTextConta)].filter(Boolean)
                    : [];

                const cellContent = (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                        <span style={{ fontWeight: 500 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={agContaText}
                                />
                            ) : (
                                agContaText
                            )}
                        </span>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={bancoNome}
                                />
                            ) : (
                                bancoNome
                            )}
                        </Typography.Text>
                    </div>
                );

                if (descricao) {
                    return (
                        <Tooltip title={descricao} placement="topLeft">
                            <span style={{ cursor: 'pointer' }}>{cellContent}</span>
                        </Tooltip>
                    );
                }

                return cellContent;
            },
        },
        {
            title: 'Fontes',
            dataIndex: ['conta', 'fontes'],
            key: 'fontes',
            filteredValue: filteredInfo.fontes || null,
            ...columnSearchPropsFontes,
            width: '14%',
            render: (_: any, record: IInvestimentoAtivacao) => (
                <FontesTagsCell fontes={record.conta?.fontes} />
            ),
        },
        {
            title: 'Órgão vinculado',
            dataIndex: ['conta', 'orgaoVinculado'],
            key: 'orgaoVinculado',
            filteredValue: filteredInfo.orgaoVinculado || null,
            ...useGetColumnSearchPropsObj('conta', 'orgaoVinculado'),
            width: '14%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) =>
                (a.conta?.orgaoVinculado?.nome || '').localeCompare(b.conta?.orgaoVinculado?.nome || ''),
            render: (_: any, record: IInvestimentoAtivacao) => record.conta?.orgaoVinculado?.nome || '-',
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Último valor</span>,
            dataIndex: 'valor',
            key: 'valor',
            width: '10%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) => (a.valor || 0) - (b.valor || 0),
            render: (valor: number | null) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {valor !== null && valor !== undefined
                        ? `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valor, true)}`
                        : '-'}
                </span>
            ),
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Mês / ano</span>,
            dataIndex: 'mes',
            key: 'mes',
            width: '8%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) =>
                ((a.ano || 0) * 12 + (a.mes || 0)) - ((b.ano || 0) * 12 + (b.mes || 0)),
            render: (_: any, record: IInvestimentoAtivacao) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {record.mes && record.ano ? `${String(record.mes).padStart(2, '0')}/${record.ano}` : '-'}
                </span>
            ),
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Atualizado em</span>,
            dataIndex: 'atualizadoEm',
            key: 'atualizadoEm',
            width: '14%',
            sorter: (a: IInvestimentoAtivacao, b: IInvestimentoAtivacao) =>
                (a.atualizadoEm ? moment(a.atualizadoEm).unix() : 0) -
                (b.atualizadoEm ? moment(b.atualizadoEm).unix() : 0),
            render: (atualizadoEm: string | null) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {atualizadoEm ? moment(atualizadoEm).format('DD/MM/YYYY HH:mm') : '-'}
                </span>
            ),
        },
    ];

    return {
        investimentos,
        loading,
        selectedRowKeys,
        canAlterarConta,
        isInvestimentoResgatado,
        investimentoParaAlterar,
        columns,
        rowSelection,
        refContagemDoFiltro,
        propsState,
        hasActiveFilters,
        setFilteredInfo,
        handleClearAllFilters,
        carregarInvestimentos,
        onclickTable,
        handleResetRowSelection,
        handleClickAlterarConta,
        handleCloseAlterarConta,
        handleSuccessAlterarConta,
    };
};
